import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SettingsSectionCard } from "@/features/seller-portal/SettingsSectionCard";
import { SELLER_PORTAL_NS } from "@/features/seller-portal/i18n";
import { useUpdateSellerProfile } from "@/features/seller-portal/hooks";
import type { MyShopProfile } from "@/features/seller-portal/api";

export function ShopProfileSection({
  profile,
  readOnly,
}: {
  profile: MyShopProfile;
  readOnly: boolean;
}): ReactElement {
  const { t } = useTranslation(SELLER_PORTAL_NS);
  const update = useUpdateSellerProfile();

  const [businessName, setBusinessName] = useState(profile.seller.business_name);
  const [pavilion, setPavilion] = useState(profile.seller.pavilion_number);
  const [hint, setHint] = useState(profile.seller.entrance_hint ?? "");
  // SCRUM-172: mirrors the backend's MyShopProfileUpdate constraints
  // (business_name min_length=2, pavilion_number min_length=1) so a blank
  // save is caught here, inline, instead of round-tripping to a 422 the UI
  // never surfaced.
  const [businessNameError, setBusinessNameError] = useState<string | null>(null);
  const [pavilionError, setPavilionError] = useState<string | null>(null);

  const onSave = (): void => {
    const nameErr =
      businessName.trim().length < 2 ? t("settings.profile.businessNameRequired") : null;
    const pavErr =
      pavilion.trim().length < 1 ? t("settings.profile.pavilionRequired") : null;
    setBusinessNameError(nameErr);
    setPavilionError(pavErr);
    if (nameErr !== null || pavErr !== null) return;

    update.mutate(
      { business_name: businessName, pavilion_number: pavilion, entrance_hint: hint },
      {
        onSuccess: () => toast.success(t("settings.saved")),
        onError: () => toast.error(t("settings.saveError")),
      },
    );
  };

  return (
    <SettingsSectionCard
      title={t("settings.sections.profile")}
      testid="settings-section-profile"
      onSave={onSave}
      saving={update.isPending}
      disabled={readOnly}
    >
      <div>
        <Label htmlFor="set-business-name">
          {t("settings.profile.businessName")}
          <span aria-hidden="true" className="text-[var(--g-error)]">
            {" "}
            *
          </span>
        </Label>
        <Input
          id="set-business-name"
          data-testid="set-business-name"
          value={businessName}
          disabled={readOnly}
          aria-required="true"
          aria-invalid={businessNameError !== null || undefined}
          aria-describedby={
            businessNameError !== null ? "set-business-name-error" : undefined
          }
          onChange={(e) => {
            setBusinessName(e.target.value);
            if (businessNameError !== null) setBusinessNameError(null);
          }}
        />
        {businessNameError !== null ? (
          <p
            id="set-business-name-error"
            role="alert"
            data-testid="set-business-name-error"
            className="mt-1 text-xs text-[var(--g-error)]"
          >
            {businessNameError}
          </p>
        ) : null}
      </div>
      <div>
        <Label htmlFor="set-market">{t("settings.profile.market")}</Label>
        <Input
          id="set-market"
          value={profile.profile.market_label ?? ""}
          disabled
          readOnly
        />
      </div>
      <div>
        <Label htmlFor="set-pavilion">
          {t("settings.profile.pavilion")}
          <span aria-hidden="true" className="text-[var(--g-error)]">
            {" "}
            *
          </span>
        </Label>
        <Input
          id="set-pavilion"
          data-testid="set-pavilion"
          value={pavilion}
          disabled={readOnly}
          aria-required="true"
          aria-invalid={pavilionError !== null || undefined}
          aria-describedby={pavilionError !== null ? "set-pavilion-error" : undefined}
          onChange={(e) => {
            setPavilion(e.target.value);
            if (pavilionError !== null) setPavilionError(null);
          }}
        />
        {pavilionError !== null ? (
          <p
            id="set-pavilion-error"
            role="alert"
            data-testid="set-pavilion-error"
            className="mt-1 text-xs text-[var(--g-error)]"
          >
            {pavilionError}
          </p>
        ) : null}
      </div>
      <div>
        <Label htmlFor="set-entrance">{t("settings.profile.entranceHint")}</Label>
        <Input
          id="set-entrance"
          data-testid="set-entrance"
          value={hint}
          disabled={readOnly}
          onChange={(e) => setHint(e.target.value)}
        />
      </div>
    </SettingsSectionCard>
  );
}
